import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {Patient} from 'gabi-api-ts/v2/patient/query/patient_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';

import {HealthReportSpotSignalDual} from '@/components/business/analytics/health-report/health-report-spot-signal-dual';
import {NoDataCell} from '@/components/business/analytics/patient-overview-cells/no-data-cell';
import {PatientsActiveTableCellHoverable} from '@/components/business/analytics/patients/patients-active-table-cell-hoverable';
import ErrorBoundary from '@/components/errors/error-boundary';
import ColoredContainer from '@/components/layout/colored-container';
import {MiniLoadingView} from '@/components/static/mini-loading-view';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {addDays} from '@/util/apidate-util';
import {getSignalTypeUnit} from '@/util/signal-type-util';

type SpotCellDualProps = {
    patient: Patient;
    signalType1: SignalType;
    signalType2: SignalType;
    handleWidgetChartClick?: (selectedDate: Date) => void;
}

function SpotCellDual({patient, signalType1, signalType2}: SpotCellDualProps) {
    const [latestPoint1Loading, latestPoint1] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getLatestBiometricValues,
        memoize: true,
        data: {
            signalType: [signalType1],
            patientId: patient.patientId,
        },
    });
    const [latestPoint2Loading, latestPoint2] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getLatestBiometricValues,
        memoize: true,
        data: {
            signalType: [signalType2],
            patientId: patient.patientId,
        },
    });

    const signalColors = (SignalDefinitions[signalType1] ?? SignalDefinitions[-1]).colors;

    if (latestPoint1Loading || !latestPoint1 || latestPoint2Loading || !latestPoint2) {
        return (
            <PatientsActiveTableCellHoverable
                hasHover={false}
                hoverElement={() => null}
            >
                <div className="text-center">
                    <MiniLoadingView />
                </div>
            </PatientsActiveTableCellHoverable>
        );
    }
    else {
        return (
            <PatientsActiveTableCellHoverable
                hasHover={true}
                hoverElement={() => (
                    <div className={'warning-overlay animate__animated animate__fadeIn animate__faster'}>
                        <ErrorBoundary>
                            <ColoredContainer color={signalColors.normal}>
                                <HealthReportSpotSignalDual
                                    patientId={patient.patientId?.id ?? ''}
                                    signalTypeMax={signalType1}
                                    signalTypeMin={signalType2}
                                    dateFrom={patient.lastRecordingDataTime!.date!}
                                    dateTo={addDays(patient.lastRecordingDataTime!.date!, 1)}
                                />
                            </ColoredContainer>
                        </ErrorBoundary>
                    </div>
                )}
            >
                {(latestPoint1.values.length===0 || !latestPoint1.values[0].hasData) ? (
                    <NoDataCell/>
                ) : (<>
                    <strong>
                        {Math.round(latestPoint1.values[0].value ?? 0)}
                        {' / '}
                        {Math.round(latestPoint2.values[0].value ?? 0)}
                    </strong>
                    {' '}
                    {getSignalTypeUnit(signalType1)}
                </>)}
            </PatientsActiveTableCellHoverable>
        );
    }
}

export {SpotCellDual};
